<template>
  <div>
    <validation-observer ref="form" novalidate>
      <section-title
        container-class="tag-badge-main-info mt-4 mb-4"
        content="X. Recomendaciones"
        icon="fas fa-clipboard-list-check"
      />
      <q-tab
        :active-tab="activeTab.index"
        nav-class="text-nowrap"
        @change="setTab"
      >
        <q-tab-item
          v-for="tab in tabs"
          :key="tab.id"
          :active="activeTab.index === tab.index"
          :has-error="!tab.valid"
          lazy
          :tabindex="tab.index"
          :title="tab.name"
        >
          <div v-for="area in tab.areas" :key="`area-${area.id}`">
            <answer
              :is-read-only="isReadOnly"
              :valid="tab.valid"
              :value="area"
            />
          </div>
        </q-tab-item>
      </q-tab>
    </validation-observer>
  </div>
</template>

<script>
//mixins
import alertMixin from '@/mixins/alert.mixin.js';

//utils
import { copy } from '@/utils/objUtils';

//components
import { QTab, QTabItem } from '@/components/common/q-tabs';
import SectionTitle from '@/shared/components/SectionTitle.vue';

//local-components
import Answer from './components/AreaAnswer.vue';

//services
import { getTen } from '@/modules/vocational-evaluation/api.js';

//constants
import Categories from '@/utils/constants/vocationalEvaluationInstrumentAreaCategory.js';

// id de la categoría "Otras" dentro de model.categories del tab Ten (es opcional, nunca debe marcar warning)
const OTHER_CATEGORY_ID = 24;

// una card cuenta como "llena" si al menos uno de sus campos (ej. Objetivo o Recomendaciones) tiene texto
function isAreaFilled(area) {
  return area.types.flatMap((t) => t.options).some((o) => o.value);
}

export default {
  name: 'VocationalEducationPartTen',
  components: {
    QTab,
    QTabItem,
    SectionTitle,
    Answer,
  },
  mixins: [alertMixin],
  props: {
    id: {
      type: [Number, String],
      default: () => null,
    },
    appointmentId: {
      type: [Number, String],
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    initialModel: {
      type: Object,
      required: false,
      default: () => null,
    },
    keepUpdates: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    isReadOnly: {
      type: Boolean,
      required: true,
      default: () => false,
    },
    valid: {
      type: Boolean,
      required: false,
      default: () => true,
    },
  },
  data() {
    return {
      activeTab: { index: 0, id: Categories.SchoolRecommendations },
      // snapshot de "¿la categoría tenía al menos una card llena?" tomado en el último intento de Someter
      categoriesFilledAtSubmit: {},
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    filled() {
      return this.initialModel?.filled ?? false;
    },
    tabs() {
      const categories = this.model.categories || [];
      return categories.map((x, index) => ({
        ...x,
        index,
        valid:
          x.id === OTHER_CATEGORY_ID ||
          this.valid ||
          this.categoriesFilledAtSubmit[x.id] !== false,
      }));
    },
  },
  watch: {
    // el warning solo debe reflejar el estado al momento de someter, no mientras el usuario escribe
    valid(value) {
      if (value) return;
      const categories = this.model.categories || [];
      this.categoriesFilledAtSubmit = Object.fromEntries(
        categories.map((x) => [x.id, x.areas.some(isAreaFilled)])
      );
    },
  },
  beforeDestroy() {
    this.$emit('update:initialModel', this.model);
  },
  async mounted() {
    this.$emit('update:disabled', true);
    await this.get();
    this.$emit('update:disabled', false);
  },
  methods: {
    setTab(tab) {
      this.activeTab = this.tabs[tab];
    },
    setModelFrom(value) {
      const data = copy(value);
      this.model = data;
      this.$emit('update:initialModel', data);
    },
    async get() {
      if (this.keepUpdates && this.filled) {
        this.setModelFrom(this.initialModel);
        return;
      }

      if (!this.appointmentId) return;
      const { data } = await getTen({
        id: this.id,
        appointmentId: this.appointmentId,
      });
      data.categories.forEach((x) =>
        x.areas.forEach((a) => (a.valid = a.valid ?? true))
      );
      this.activeTab = data.categories[0] || this.activeTab;
      this.setModelFrom({ ...data, filled: true });
    },
    async validate() {
      return await this.$refs.form.validate();
    },
  },
};
</script>
<style scoped></style>
